/**
 * auth.ts — autentiseringslogikk (token-lagring, JWT-decode, rolle-mapping)
 *
 * Håndterer KUN lesing/skriving av token og brukerinfo fra storage.
 * Ingen React-state — det gjøres i src/contexts/AuthContext.tsx.
 * Ingen HTTP-kall — det gjøres i src/lib/api.ts.
 *
 * VIKTIG: Vi tolker IKKE role_id til UserRole i frontend. Backend er kilde til sannhet.
 * Frontend bruker role_id direkte for tilgangskontroll (hasRole, navigation).
 *
 * Funksjoner:
 * 1. decodeJwtPayload — dekoder JWT-payload uten verifisering av signatur
 * 2. storeToken — lagrer token i localStorage eller sessionStorage
 * 3. getToken — henter token fra storage (localStorage først, så sessionStorage)
 * 4. clearToken — fjerner token fra begge storage-mekanismer
 * 5. getCurrentUser — henter token, dekoder JWT, returnerer AuthUser (med role_id)
 * 6. isTokenExpired — sjekker om token har utløpt (exp vs nå)
 * 7. isAuthenticated — sjekker om bruker er autentisert (token + ikke utløpt)
 * 8. hasRole — sjekker om bruker har en av de angitte role_id-ene
 * 9. getForhandlerNames — henter kosmetiske forhandlernavn fra JWT-payload
 * 10. getForhandlerEntries — henter (id, navn)-par fra JWT-payload
 */

import type { AuthUser, JwtPayload } from '@/types/auth';
import { normalizeIds } from '@/lib/ids';

/**
 * Storage-nøkkel for JWT-token. Brukes i både localStorage og sessionStorage.
 */
export const TOKEN_STORAGE_KEY = 'auth_token';

/**
 * 2. decodeJwtPayload — dekoder JWT-payload uten signaturverifisering
 *
 * JWT-format: header.payload.signature (punktum-separert, base64url)
 * Vi dekoder KUN payload-delen for å lese user info + exp.
 * Signatur verifiseres av backend ved hvert API-kall.
 *
 * @param token - JWT-streng
 * @returns JwtPayload eller null ved ugyldig token
 */
export function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Base64url → base64 → JSON
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = atob(base64);

    return normalizeIds(JSON.parse(jsonPayload)) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * 3. storeToken — lagrer token i localStorage eller sessionStorage
 *
 * - remember=true → localStorage (persistent, overlever nettleserlukking)
 * - remember=false → sessionStorage (slettes når fanen lukkes)
 *
 * @param token - JWT fra login-response
 * @param remember - om innlogging skal huskes
 */
export function storeToken(token: string, remember: boolean): void {
  if (remember) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    // Fjern fra sessionStorage for å unngå duplikat
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  } else {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    // Fjern fra localStorage for å unngå duplikat
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

/**
 * 4. getToken — henter token fra storage
 *
 * Sjekker localStorage først, deretter sessionStorage.
 * Returnerer null hvis token ikke finnes i noen av dem.
 *
 * @returns token-streng eller null
 */
export function getToken(): string | null {
  // Sjekk localStorage først (persistent innlogging)
  const localToken = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (localToken) return localToken;

  // Deretter sessionStorage (per-fane innlogging)
  return sessionStorage.getItem(TOKEN_STORAGE_KEY);
}

/**
 * 5. clearToken — fjerner token fra begge storage-mekanismer
 *
 * Brukes ved logout og ved utløpt token.
 */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * 6. getCurrentUser — returnerer innlogget bruker fra token
 *
 * Les kun fra storage — synkron funksjon.
 * Returnerer null hvis token mangler eller er ugyldig.
 *
 * @returns AuthUser eller null
 */
export function getCurrentUser(): AuthUser | null {
  const token = getToken();
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  return {
    id: payload.user_id,
    username: payload.username,
    role_id: payload.role_id,
  };
}

/**
 * 7. isTokenExpired — sjekker om token har utløpt
 *
 * Sammenligner exp (expiry) i JWT med nåværende tid (sekunder).
 * Returnerer true hvis token mangler, er ugyldig, eller utløpt.
 *
 * @returns true hvis token er utløpt eller ugyldig
 */
export function isTokenExpired(): boolean {
  const token = getToken();
  if (!token) return true;

  const payload = decodeJwtPayload(token);
  if (!payload) return true;

  // exp er i sekunder (Unix timestamp), Date.now() er i millisekunder
  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now;
}

/**
 * 7b. getTokenExpiryMs — returnerer tidspunktet tokenet utløper, i ms.
 *
 * Brukes av AuthProvider til å planlegge automatisk utlogging NÅR
 * JWT-nøkkelen expires (se SESSION_EXPIRY_LEAD_MS der).
 *
 * @returns exp som millisekunder (Unix), eller null hvis token mangler/er ugyldig
 */
export function getTokenExpiryMs(): number | null {
  const token = getToken();
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  // exp er i sekunder → konverter til millisekunder
  return payload.exp * 1000;
}

/**
 * 7c. getRoleDepartmentNames — avdelingene rollen har tilgang til, fra JWT (`role_department_names`, as-is)
 *
 * Avgjør hvilke rotelementer (menyer med `department`) i sidemenyen brukeren får se. Mangler token eller feltet, returneres tom liste.
 */
export function getRoleDepartmentNames(): string[] {
  const token = getToken();
  if (!token) return [];
  return decodeJwtPayload(token)?.role_department_names ?? [];
}

/**
 * 8. isAuthenticated — sjekker om bruker er autentisert
 *
 * Bruker er autentisert hvis token finnes og ikke er utløpt.
 *
 * @returns true hvis bruker er autentisert
 */
export function isAuthenticated(): boolean {
  return getToken() !== null && !isTokenExpired();
}

/**
 * 8. hasRole — sjekker om bruker har en av de angitte rollene
 *
 * NB: Tar nå imot role_id (number) i stedet for UserRole (string).
 * Frontend vet IKKE hva role_id betyr — det er backend som eier denne informasjonen.
 * Navigation-config bruker `role_ids: number[]` for å definere tilgang.
 *
 * Bruk:
 *   if (hasRole(user, [1])) { ... }  // 1 = admin (per API.md)
 *
 * @param user - innlogget bruker (eller null)
 * @param roleIds - liste over role_id-er som har tilgang
 * @returns true hvis bruker har en av rollene
 */
export function hasRole(user: AuthUser | null, roleIds: string[]): boolean {
  if (!user || user.role_id === undefined) return false;
  return roleIds.includes(user.role_id);
}

/**
 * 9. getForhandlerNames — henter kosmetiske forhandlernavn fra JWT-payload
 *
 * Leser `forhandler_name_cosmetic`-mappet fra JWT (as-is fra backend).
 * Returnerer kun navn-verdiene — frontend tolker ikke nøkkel/innhold.
 * Valgfritt felt: tom liste hvis token mangler, er ugyldig, eller mappet
 * ikke finnes (eldre tokens).
 *
 * @returns liste med forhandlernavn (strenger), tom ved fravær
 */
export function getForhandlerNames(): string[] {
  const token = getToken();
  if (!token) return [];

  const payload = decodeJwtPayload(token);
  if (!payload || !payload.forhandler_name_cosmetic) return [];

  return Object.values(payload.forhandler_name_cosmetic);
}

/**
 * 10. getForhandlerEntries — henter (id, navn)-par fra JWT-payload
 *
 * Leser `forhandler_name_cosmetic`-mappet fra JWT (as-is fra backend).
 * Nøklene er forhandler-ID-er (strenger) — brukes til å sette aktiv
 * forhandler i storage. Verdiene er kosmetiske navn (kun visning).
 *
 * @returns liste med { id, name }, tom ved fravær
 */
export function getForhandlerEntries(): Array<{ id: string; name: string }> {
  const token = getToken();
  if (!token) return [];

  const payload = decodeJwtPayload(token);
  if (!payload || !payload.forhandler_name_cosmetic) return [];

  return Object.entries(payload.forhandler_name_cosmetic).map(([id, name]) => ({
    id,
    name,
  }));
}
